/*
  rg-gallery-lightbox.js — The Rustic Grove Co.
  Product gallery expanded view (all viewports). Clicking the main image opens a
  full-screen view with the enlarged image, a thumbnail strip and a close button.
  Navigate between images with the on-screen arrows, the keyboard (← →) or swipe.
  Arrow keys also move the MAIN gallery when the expanded view is closed.
  Dawn's own modal is suppressed (capture-phase interception).

  Combination filter: thumbs are tagged data-finish / data-leg-colour /
  data-leg-style server-side and hidden when they don't match the current
  finish + leg colour + leg style. The strip and the arrow navigation operate on
  the VISIBLE subset only, and re-filter on variantChange (finish) and
  leg:change (legs) — mirroring the main gallery (rg-gallery-filter.js).
  Degrades gracefully: a full-combination match that yields nothing falls back to
  finish-only, then to all thumbs — never empty.
*/
(function () {
  function init(lb) {
    var sheet = lb.querySelector('.rg-lightbox__sheet');
    var mainImg = lb.querySelector('[data-rg-lb-main]');
    var thumbs = Array.prototype.slice.call(lb.querySelectorAll('[data-rg-lb-thumb]'));
    var closeBtn = lb.querySelector('[data-rg-lb-close]');
    var prevBtn = lb.querySelector('[data-rg-lb-prev]');
    var nextBtn = lb.querySelector('[data-rg-lb-next]');
    var lastFocus = null;
    if (!thumbs.length) return;

    // Fade-in plumbing: late-arriving full-size images fade in COMPLETE
    // (see setActive) instead of painting progressively — never small-then-big.
    mainImg.style.transition = 'opacity 0.15s ease';

    // Move to <body> so position:fixed is relative to the viewport, not a
    // transformed/sticky Dawn ancestor.
    if (lb.parentNode !== document.body) document.body.appendChild(lb);

    // --- Current selection (kept in sync with the main gallery) ---
    var galleryRoot = document.querySelector('media-gallery[data-finish-option-index], media-gallery[data-leg-filter]');
    var legSelector = document.querySelector('[data-rg-leg-selector]');
    var finishIndex = galleryRoot ? parseInt(galleryRoot.getAttribute('data-finish-option-index'), 10) : NaN;

    function attr(el, name) {
      return ((el && el.getAttribute(name)) || '').toLowerCase();
    }
    var curFinish = attr(galleryRoot, 'data-current-finish');
    var curLegColour = attr(legSelector, 'data-current-leg-colour') || attr(galleryRoot, 'data-current-leg-colour');
    var curLegStyle = attr(legSelector, 'data-current-leg-style') || attr(galleryRoot, 'data-current-leg-style');

    // Dawn prefixes gallery media ids (e.g. "template--123__main-456"); our
    // thumbs use the raw media id. Match on the trailing numeric id.
    function norm(v) {
      var m = String(v).match(/(\d+)$/);
      return m ? m[1] : String(v);
    }
    function visible() {
      return thumbs.filter(function (t) { return !t.classList.contains('hidden'); });
    }
    function thumbById(id) {
      return thumbs.filter(function (t) { return norm(t.getAttribute('data-media-id')) === norm(id); })[0];
    }

    function axisMatch(token, current) {
      if (!current) return true;
      if (token == null) return true;
      return token === 'all' || token === current;
    }
    function fullMatch(t) {
      return (
        axisMatch(t.getAttribute('data-finish'), curFinish) &&
        axisMatch(t.getAttribute('data-leg-colour'), curLegColour) &&
        axisMatch(t.getAttribute('data-leg-style'), curLegStyle)
      );
    }
    function finishOnlyMatch(t) {
      return axisMatch(t.getAttribute('data-finish'), curFinish);
    }

    function applyFilter() {
      var predicate = fullMatch;
      if (!thumbs.filter(predicate).length) predicate = finishOnlyMatch;
      if (!thumbs.filter(predicate).length) predicate = function () { return true; };

      thumbs.forEach(function (t) { t.classList.toggle('hidden', !predicate(t)); });

      // If the active thumb is now hidden (or none active), move to first visible.
      var active = thumbs.filter(function (t) { return t.classList.contains('is-active'); })[0];
      if (!active || active.classList.contains('hidden')) {
        var vis = visible();
        if (vis.length) setActive(vis[0].getAttribute('data-media-id'), true);
      }
    }

    function activeIndexInVisible() {
      var vis = visible();
      for (var i = 0; i < vis.length; i++) {
        if (vis[i].classList.contains('is-active')) return i;
      }
      return 0;
    }

    // Pre-warm: download a thumb's full-size image ahead of need so the stage
    // shows it instantly from cache. Full resolution only — no low-res swaps.
    function preloadFull(t) {
      try {
        if (!t) return;
        var u = t.getAttribute('data-full');
        if (!u) return;
        var im = new Image();
        im.src = u;
      } catch (e) {
        /* non-fatal */
      }
    }
    function preloadNeighbours() {
      try {
        var vis = visible();
        if (vis.length < 2) return;
        var i = activeIndexInVisible();
        preloadFull(vis[(i + 1) % vis.length]);
        preloadFull(vis[(i - 1 + vis.length) % vis.length]);
      } catch (e) {
        /* non-fatal */
      }
    }

    function setActive(id, quiet) {
      thumbs.forEach(function (t) {
        var on = norm(t.getAttribute('data-media-id')) === norm(id);
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
        if (on) {
          // Only load the full-size image while the lightbox is actually open.
          // Setting it while closed downloaded a ~1400px image on every page
          // view; open() re-runs setActive, so the src is set on first open.
          if (!lb.hidden) {
            // Hold the frame invisible until the COMPLETE image is ready, then
            // fade in. Cached (the normal case after pre-warming) resolves
            // synchronously below — instant and sharp, no perceptible fade.
            mainImg.style.opacity = '0';
            mainImg.onload = function () { mainImg.style.opacity = '1'; };
            mainImg.src = t.getAttribute('data-full');
            if (mainImg.complete && mainImg.naturalWidth) mainImg.style.opacity = '1';
            var im = t.querySelector('img');
            mainImg.alt = im ? im.alt : '';
          }
          if (!quiet) t.scrollIntoView({ inline: 'center', block: 'nearest' });
        }
      });
      // Keep one step ahead of arrow/swipe navigation.
      if (!lb.hidden) preloadNeighbours();
    }

    function nav(dir) {
      var vis = visible();
      if (!vis.length) return;
      var n = (activeIndexInVisible() + dir + vis.length) % vis.length;
      setActive(vis[n].getAttribute('data-media-id'));
    }

    function open(id) {
      var t = thumbById(id);
      if (!t || t.classList.contains('hidden')) {
        var vis = visible();
        if (!vis.length) return;
        id = vis[0].getAttribute('data-media-id');
      }
      lastFocus = document.activeElement;
      lb.hidden = false;
      document.documentElement.classList.add('rg-lb-open');
      setActive(id);
      closeBtn.focus();
      preloadNeighbours();
    }

    function close() {
      lb.hidden = true;
      document.documentElement.classList.remove('rg-lb-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { setActive(t.getAttribute('data-media-id')); });
    });
    closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function () { nav(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { nav(1); });
    lb.addEventListener('click', function (e) { if (e.target === lb || e.target === sheet) close(); });

    document.addEventListener('keydown', function (e) {
      if (lb.hidden) return;
      if (e.key === 'Escape') { close(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); nav(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); nav(-1); }
    });

    // Touch swipe on the main image.
    var sx = null;
    var stage = lb.querySelector('.rg-lightbox__stage');
    if (stage) {
      // Anti-collapse: hold the frame while an image is in flight so a late
      // arrival never renders as a layout jump.
      stage.style.minHeight = '60vh';
      stage.addEventListener('touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (sx === null) return;
        var dx = e.changedTouches[0].clientX - sx;
        if (Math.abs(dx) > 40) nav(dx < 0 ? 1 : -1);
        sx = null;
      });
    }

    // Open on click of the MAIN gallery image (any viewport). Thumbnails just
    // switch the main image, so ignore clicks on them. Capture phase + stop so
    // Dawn's own modal doesn't also fire.
    var gallery = document.querySelector('.product__media-wrapper') || document.querySelector('.product__column-sticky');
    if (gallery) {
      gallery.addEventListener('click', function (e) {
        if (lb.contains(e.target)) return;
        if (e.target.closest('.thumbnail-list, .thumbnail-slider')) return;
        var item = e.target.closest('.product__media-item, [data-media-id]');
        if (!item) return;
        var idEl = item.matches('[data-media-id]') ? item : item.querySelector('[data-media-id]');
        var id = idEl && idEl.getAttribute('data-media-id');
        if (!id || !thumbs.some(function (t) { return norm(t.getAttribute('data-media-id')) === norm(id); })) return;
        e.preventDefault();
        e.stopPropagation();
        open(id);
      }, true);

      // Arrow keys cycle the MAIN gallery image while on the product page — only
      // through the currently-visible (non-hidden) thumbnails.
      document.addEventListener('keydown', function (e) {
        if (!lb.hidden) return;
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var ae = document.activeElement;
        if (ae && (/^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName) || ae.isContentEditable)) return;
        var tbtns = Array.prototype.slice.call(gallery.querySelectorAll('.thumbnail-list__item:not(.hidden) button'));
        if (tbtns.length < 2) return;
        var idx = tbtns.findIndex(function (b) { return b.getAttribute('aria-current') === 'true'; });
        if (idx < 0) idx = 0;
        var n = e.key === 'ArrowRight' ? (idx + 1) % tbtns.length : (idx - 1 + tbtns.length) % tbtns.length;
        e.preventDefault();
        tbtns[n].click();
      });
    }

    // --- Keep the strip in sync with the current selection ---
    applyFilter();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
        try {
          var data = evt && evt.data;
          if (!data || !data.variant) return;
          if (finishIndex >= 0) {
            var opts = data.variant.options || [];
            curFinish = (opts[finishIndex] || '').toLowerCase();
          }
          applyFilter();
        } catch (e) {
          /* non-fatal */
        }
      });
    }

    document.addEventListener('leg:change', function (evt) {
      try {
        var d = evt && evt.detail;
        if (!d) return;
        curLegStyle = (d.style || '').toLowerCase();
        curLegColour = (d.colour || '').toLowerCase();
        applyFilter();
      } catch (e) {
        /* non-fatal */
      }
    });

    // Instant finish response: re-filter the moment the swatch is tapped instead
    // of waiting for Dawn's section fetch (variantChange) to come back — that
    // round-trip left the old finish's thumbs on screen for ~0.5-1s. Delegated on
    // document because Dawn re-renders variant-selects after each change. The
    // later variantChange re-apply is an idempotent no-op.
    document.addEventListener('change', function (evt) {
      try {
        var inp = evt.target;
        if (!inp || inp.type !== 'radio') return;
        if (!/^(finish|colour|color)-\d+$/i.test(inp.name || '')) return;
        if (!inp.checked) return;
        curFinish = (inp.value || '').toLowerCase();
        applyFilter();
      } catch (e) {
        /* non-fatal */
      }
    });

    // Post-load idle warm: fetch the ACTIVE thumb's full-size image 800ms after
    // `load` (clear of LCP) so the first lightbox open is instant from cache.
    function idleWarm() {
      var t = thumbs.filter(function (x) { return x.classList.contains('is-active'); })[0] || visible()[0];
      preloadFull(t);
    }
    if (document.readyState === 'complete') {
      setTimeout(idleWarm, 800);
    } else {
      window.addEventListener('load', function () { setTimeout(idleWarm, 800); }, { once: true });
    }
  }

  function boot() { document.querySelectorAll('[data-rg-lightbox]').forEach(init); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
